/**
 * New intake — the paralegal on the phone with somebody who needs a lawyer.
 * @file src/web/shared/intake/NewIntakeDialog.tsx
 *
 * Two steps, from the top bar of any screen:
 *
 *   1. Which intake. With more than one, the paralegal picks the form that
 *      fits the call — a general enquiry first, since most calls are not yet
 *      one kind of matter. With only one, this step is skipped.
 *   2. The form itself. The same questions the client would meet online, in
 *      the same order and with the same conditions, sent through the same
 *      door (POST /api/form/:id/intake), so a call and a website enquiry make
 *      the same prospect, draft matter and score.
 *
 * Nothing is sent to them from here. The prospect's page opens, and what
 * happens next — a welcome, or a welcome with the retainer — is chosen there
 * (pipeline/components/NextStep.tsx), where the form's playbook suggests it.
 *
 * @llm-rule WHEN: Staff take a new enquiry by phone or in person
 * @llm-rule AVOID: Sending anything from the popup — that is the prospect page's job
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, FormField, Input, Textarea, cn, toast } from '@bloomneo/uikit';
import { ArrowLeft, ArrowRight, ClipboardList, Loader2, Paperclip, Plus, X } from 'lucide-react';
import { api } from '@/shared/api';
import { usePracticeAreas } from '@/shared/hooks/usePracticeAreas';
import { answerProblem, answered, filledRows, hasAnswer } from '@shared/answers';
import { languageName } from '@shared/languages';

type Text = Record<string, string>;

interface Question {
  id: string;
  type: string;
  label: Text;
  help?: Text;
  required?: boolean;
  options?: { value: string; label: Text }[];
  showIf?: { questionId: string; equals: string[]; mode?: 'is' | 'isNot' };
  fields?: Question[];
  max?: number;
  mapsTo?: string;
  min?: number;
  maxValue?: number;
}

interface Form {
  id: string;
  name: string;
  description: string | null;
  matterType: string | null;
  isPublished: boolean;
  languages: string[];
  questions: Question[];
}

type Answers = Record<string, unknown>;

const SILENT = new Set(['section', 'instruction']);
/** What says who they are; one of them is needed for there to be a prospect. */
const IDENTITY = new Set(['name', 'email', 'phone']);

/** The English wording, or the first there is. */
const en = (t?: Text) => t?.en ?? Object.values(t ?? {})[0] ?? '';

/** Shown unless its condition says otherwise — the server's own rule (wasAsked). */
function visible(q: Question, answers: Answers) {
  if (!q.showIf) return true;
  const on = answers[q.showIf.questionId];
  const given = Array.isArray(on) ? on.map(String) : [String(on ?? '')];
  const matches = q.showIf.equals.some((v) => given.includes(v));
  return q.showIf.mode === 'isNot' ? !matches : matches;
}

/** What is wrong with one answer, in the words the form would use. */
function problemOf(q: Question, value: unknown): string | null {
  if (q.required && !answered(value)) return 'Required';
  const bad = answerProblem(q, value);
  if (bad) return bad === 'email' ? 'Does not look like an email address' : 'Does not look like a phone number';
  if (q.type === 'number' && typeof value === 'string' && value.trim()) {
    const n = Number(value.replace(/,/g, ''));
    if (Number.isNaN(n)) return 'A number, please';
    if (q.min != null && n < q.min) return `At least ${q.min}`;
    if (q.maxValue != null && n > q.maxValue) return `At most ${q.maxValue}`;
  }
  return null;
}

/** Every answer the server would refuse, keyed by question (and row). */
function problemsIn(questions: Question[], answers: Answers): Record<string, string> {
  const out: Record<string, string> = {};
  for (const q of questions) {
    if (SILENT.has(q.type) || q.type === 'file' || !visible(q, answers)) continue;
    if (q.type === 'group') {
      if (q.required && !hasAnswer(q, answers)) out[q.id] = 'Add at least one';
      filledRows(answers[q.id]).forEach((row, i) => {
        for (const f of q.fields ?? []) {
          const p = problemOf(f, row[f.id]);
          if (p) out[`${q.id}.${i}.${f.id}`] = p;
        }
      });
      continue;
    }
    const p = problemOf(q, answers[q.id]);
    if (p) out[q.id] = p;
  }
  return out;
}

export function NewIntakeDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const nav = useNavigate();
  const { labelFor } = usePracticeAreas();
  const [forms, setForms] = useState<Form[] | null>(null);
  const [formId, setFormId] = useState('');
  const [answers, setAnswers] = useState<Answers>({});
  const [language, setLanguage] = useState('en');
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState(false);
  const body = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    setAnswers({});
    setChecked(false);
    setLanguage('en');
    setForms(null);
    void api
      .get<{ items: Form[] }>('/api/form/list')
      .then((r) => {
        /* A general enquiry first: most calls are not yet one kind of matter. */
        const live = r.items.filter((f) => f.isPublished).sort((a, b) => Number(b.matterType === 'general') - Number(a.matterType === 'general'));
        setForms(live);
        /* One intake: nothing to choose, straight to the questions. */
        setFormId(live.length === 1 ? live[0].id : '');
      })
      .catch(() => setForms([]));
  }, [open]);

  const form = forms?.find((f) => f.id === formId) ?? null;
  const questions = form?.questions ?? [];
  const shown = questions.filter((q) => visible(q, answers));
  const problems = useMemo(() => problemsIn(questions, answers), [questions, answers]);
  const asksWho = questions.some((q) => q.mapsTo && IDENTITY.has(q.mapsTo));
  const knowWho = questions.some((q) => q.mapsTo && IDENTITY.has(q.mapsTo) && visible(q, answers) && answered(answers[q.id]));
  const nameOf = String(answers[questions.find((q) => q.mapsTo === 'name')?.id ?? ''] ?? '').trim();

  const set = (id: string, v: unknown) => setAnswers((a) => ({ ...a, [id]: v }));

  const pick = (id: string) => {
    setFormId(id);
    setAnswers({});
    setChecked(false);
    body.current?.scrollTo({ top: 0 });
  };

  const create = async () => {
    setChecked(true);
    if (Object.keys(problems).length || !knowWho) {
      /* Take them to the first thing to fix, not a toast about it. */
      requestAnimationFrame(() => body.current?.querySelector('[data-problem="true"]')?.scrollIntoView({ block: 'center', behavior: 'smooth' }));
      return;
    }
    setBusy(true);
    try {
      /* Only what was asked: an answer behind a condition they never met is not theirs. */
      const asked = Object.fromEntries(Object.entries(answers).filter(([id]) => shown.some((q) => q.id === id)));
      const r = await api.post<{ leadId: string | null; leadName: string | null; existing: boolean }>(`/api/form/${form!.id}/intake`, { answers: asked, language });
      if (!r.leadId) throw new Error('The form was saved, but it names nobody — add their name, email or phone.');
      if (r.existing) toast.info(`${r.leadName ?? 'They'} ${r.leadName ? 'is' : 'are'} already a prospect`, { description: `Same email — this ${form!.name.toLowerCase()} is added to their page.` });
      else toast.success(`${r.leadName || nameOf || 'Prospect'} added`, { description: 'Choose what to send them from their page.' });
      onOpenChange(false);
      nav(`/pipeline/${r.leadId}`);
    } catch (err) {
      toast.error('Not added', { description: err instanceof Error ? err.message : String(err) });
    } finally {
      setBusy(false);
    }
  };

  const choosing = !form;
  const several = (forms?.length ?? 0) > 1;

  return (
    <Dialog open={open} onOpenChange={(v) => !busy && onOpenChange(v)}>
      <DialogContent className="flex max-h-[94vh] grid-cols-[minmax(0,1fr)] flex-col gap-0 overflow-hidden p-0 sm:max-w-2xl">
        <DialogHeader className="border-b border-border px-6 pb-4 pt-6">
          <DialogTitle>{choosing ? 'New intake' : form.name}</DialogTitle>
          <DialogDescription>
            {choosing
              ? 'Which form fits this call? You fill it in as you talk; their prospect page opens when you are done.'
              : 'Ask what you can — anything you skip, they can fill in later. Nothing is sent to them yet.'}
          </DialogDescription>
          {!choosing && (several || form.languages.length > 1) && (
            <div className="flex flex-wrap items-center gap-2 pt-2">
              {several && (
                <Button size="sm" variant="ghost" className="-ml-2 h-7 text-xs" disabled={busy} onClick={() => setFormId('')}>
                  <ArrowLeft className="mr-1 size-3.5" /> Another form
                </Button>
              )}
              {form.languages.length > 1 && (
                <label className="ml-auto flex items-center gap-2 text-xs text-muted-foreground">
                  They speak
                  <select value={language} onChange={(e) => setLanguage(e.target.value)} className="h-7 rounded-md border border-border bg-background px-2 text-xs text-foreground">
                    {form.languages.map((l) => (
                      <option key={l} value={l}>
                        {languageName(l, 'english')}
                      </option>
                    ))}
                  </select>
                </label>
              )}
            </div>
          )}
        </DialogHeader>

        <div ref={body} className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
          {forms === null ? (
            <div className="h-32 animate-pulse rounded-xl bg-muted" />
          ) : forms.length === 0 ? (
            <p className="rounded-xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
              No published intake forms yet. Publish one under Forms, then take the call here.
            </p>
          ) : choosing ? (
            <div className="grid gap-2 sm:grid-cols-2" aria-label="Intake form">
              {forms.map((f) => {
                const count = f.questions.filter((q) => !SILENT.has(q.type)).length;
                return (
                  <button
                    key={f.id}
                    type="button"
                    onClick={() => pick(f.id)}
                    className="group rounded-xl border border-border p-4 text-left transition hover:border-primary/50 hover:bg-primary/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <span className="flex items-center gap-2 text-sm font-medium">
                      <ClipboardList className="size-4 text-muted-foreground group-hover:text-primary" aria-hidden="true" /> {f.name}
                      <ArrowRight className="ml-auto size-3.5 text-muted-foreground opacity-0 transition group-hover:opacity-100" aria-hidden="true" />
                    </span>
                    {f.description && <span className="mt-1 line-clamp-2 block text-xs text-muted-foreground">{f.description}</span>}
                    <span className="mt-2 block text-[11px] text-muted-foreground">
                      {f.matterType && f.matterType !== 'general' ? `${labelFor(f.matterType)} · ` : ''}
                      {count} question{count === 1 ? '' : 's'}
                    </span>
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="space-y-4">
              {!asksWho && (
                <p className="rounded-lg border border-warning/40 bg-warning/10 px-3 py-2 text-xs">
                  This form never asks for a name, email or phone, so it cannot make a prospect. Add one under Forms, or pick another.
                </p>
              )}
              {shown.map((q) => {
                if (q.type === 'section')
                  return (
                    <h3 key={q.id} className="border-b border-border pb-1.5 pt-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground first:pt-0">
                      {en(q.label)}
                    </h3>
                  );
                if (q.type === 'instruction')
                  return (
                    <p key={q.id} className="whitespace-pre-wrap rounded-lg bg-muted/50 px-3 py-2 text-xs leading-relaxed text-muted-foreground">
                      {en(q.label)}
                    </p>
                  );
                const problem = checked ? problems[q.id] : undefined;
                if (q.type === 'file')
                  return (
                    <div key={q.id} className="flex items-start gap-2.5 rounded-lg border border-dashed border-border px-3 py-2.5 text-sm">
                      <Paperclip className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                      <span>
                        {en(q.label)}
                        <span className="block text-xs text-muted-foreground">Ask them for it from their page, under Request documents.</span>
                      </span>
                    </div>
                  );
                return (
                  <div key={q.id} data-problem={Boolean(problem)}>
                    <FormField label={en(q.label)} required={q.required} helper={en(q.help) || undefined} error={problem}>
                      {q.type === 'group' ? (
                        <Rows q={q} value={answers[q.id]} onChange={(v) => set(q.id, v)} problems={checked ? problems : {}} />
                      ) : (
                        <Answer q={q} value={answers[q.id]} onChange={(v) => set(q.id, v)} autoFocus={q === shown.find((x) => !SILENT.has(x.type))} />
                      )}
                    </FormField>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {form && (
          <div className="flex items-center justify-between gap-3 border-t border-border bg-muted/30 px-6 py-3">
            <p className={cn('text-xs', checked && !knowWho ? 'text-destructive' : 'text-muted-foreground')} data-problem={checked && !knowWho}>
              {checked && !knowWho ? 'Add their name, email or phone first.' : checked && Object.keys(problems).length ? `${Object.keys(problems).length} to fix above` : 'Only what is marked required is needed now.'}
            </p>
            <Button disabled={busy || !asksWho} onClick={() => void create()}>
              {busy && <Loader2 className="mr-1.5 size-4 animate-spin" />}
              Create prospect
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/** What FormField hands its child so the label and any error belong to the control. */
interface Control {
  id?: string;
  'aria-invalid'?: boolean;
  'aria-describedby'?: string;
}

/** One answer, the shape the form keeps it in: yes/no, a choice's value, a list of them, text. */
function Answer({ q, value, onChange, autoFocus, ...control }: { q: Question; value: unknown; onChange: (v: unknown) => void; autoFocus?: boolean } & Control) {
  const options = q.type === 'yesno' ? [{ value: 'yes', label: { en: 'Yes' } }, { value: 'no', label: { en: 'No' } }] : (q.options ?? []);

  /* A short list reads faster as buttons than as a menu, on a call. */
  if (q.type === 'yesno' || ((q.type === 'choice' || q.type === 'dropdown') && options.length <= 4))
    return (
      <div {...control} className="flex flex-wrap gap-1.5" role="radiogroup">
        {options.map((o) => (
          <Button key={o.value} type="button" size="sm" role="radio" aria-checked={value === o.value} variant={value === o.value ? 'default' : 'outline'} onClick={() => onChange(value === o.value ? '' : o.value)}>
            {en(o.label)}
          </Button>
        ))}
      </div>
    );
  if (q.type === 'choice' || q.type === 'dropdown')
    return (
      <select {...control} value={(value as string) ?? ''} onChange={(e) => onChange(e.target.value)} className="h-9 w-full rounded-md border border-border bg-background px-3 text-sm">
        <option value="">—</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {en(o.label)}
          </option>
        ))}
      </select>
    );
  if (q.type === 'multiselect') {
    const list = Array.isArray(value) ? (value as string[]) : [];
    return (
      <div {...control} role="group" className="flex flex-wrap gap-1.5">
        {options.map((o) => {
          const on = list.includes(o.value);
          return (
            <Button key={o.value} type="button" size="sm" aria-pressed={on} variant={on ? 'default' : 'outline'} onClick={() => onChange(on ? list.filter((x) => x !== o.value) : [...list, o.value])}>
              {en(o.label)}
            </Button>
          );
        })}
      </div>
    );
  }
  if (q.type === 'textarea') return <Textarea {...control} rows={3} autoFocus={autoFocus} value={(value as string) ?? ''} onChange={(e) => onChange(e.target.value)} />;
  const rule = q.type === 'email' || q.mapsTo === 'email' ? 'email' : q.type === 'phone' || q.mapsTo === 'phone' ? 'tel' : null;
  return (
    <Input
      {...control}
      autoFocus={autoFocus}
      type={q.type === 'date' ? 'date' : rule ?? 'text'}
      inputMode={q.type === 'number' ? 'decimal' : rule === 'tel' ? 'tel' : undefined}
      value={(value as string) ?? ''}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

/** A repeating block — providers, witnesses — as rows of the same few answers. */
function Rows({ q, value, onChange, problems, ...control }: { q: Question; value: unknown; onChange: (v: unknown) => void; problems: Record<string, string> } & Control) {
  const rows = Array.isArray(value) ? (value as Answers[]) : [];
  const fields = q.fields ?? [];
  const max = q.max ?? 50;
  const setRow = (i: number, id: string, v: unknown) => onChange(rows.map((r, j) => (j === i ? { ...r, [id]: v } : r)));
  return (
    <div {...control} role="group" className="space-y-2">
      {rows.map((row, i) => (
        <div key={i} className="relative grid gap-3 rounded-lg border border-border p-3 pr-10 sm:grid-cols-2">
          {fields.map((f) => (
            <FormField key={f.id} label={en(f.label)} required={f.required} error={problems[`${q.id}.${i}.${f.id}`]}>
              <Answer q={f} value={row[f.id]} onChange={(v) => setRow(i, f.id, v)} />
            </FormField>
          ))}
          <Button type="button" size="icon" variant="ghost" className="absolute right-1.5 top-1.5 size-7" aria-label={`Remove row ${i + 1}`} onClick={() => onChange(rows.filter((_, j) => j !== i))}>
            <X className="size-3.5" />
          </Button>
        </div>
      ))}
      {rows.length < max && (
        <Button type="button" size="sm" variant="outline" onClick={() => onChange([...rows, {}])}>
          <Plus className="mr-1.5 size-3.5" /> {rows.length ? 'Add another' : 'Add one'}
        </Button>
      )}
    </div>
  );
}
